'use client';

import { forwardRef, useContext, useId } from 'react';

import { css, cx } from '@/styled-system/css';

import { useIsomorphicLayoutEffect } from '../../hooks/use-isomorphic-layout-effect';
import { PopoverRootContext } from './context/popover-root.context';
import { popoverBodyStyles } from './styles/popover-body.styles';
import type { PopoverBodyPropsInterface } from './types/popover-body.types';

/**
 * @since 10.0.0
 */
export const PopoverBody = forwardRef<HTMLDivElement, PopoverBodyPropsInterface>(
  (props: PopoverBodyPropsInterface, forwardedRef) => {
    const { children, className, css: cssProp, ...rest } = props;
    const { popover } = useContext(PopoverRootContext);
    const id = useId();
    const styles = css(popoverBodyStyles, cssProp);

    useIsomorphicLayoutEffect(() => {
      popover.setDescriptionId(id);
      return () => popover.setDescriptionId(undefined);
    }, [id, popover.setDescriptionId]);

    return (
      <div
        ref={forwardedRef}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Popover.Body"
        id={id}
        className={cx('c11n', 'c11n_popover__body', styles, className)}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

PopoverBody.displayName = 'Popover.Body';
